
(() => {
  const MODAL_SELECTOR = '[data-hk-compare-modal]';
  const OPEN_SELECTOR = '[data-hk-compare-open]';

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normalize = (value) => String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  function parseProducts(modal) {
    const script = modal.querySelector('[data-hk-compare-products]');
    if (!script) return [];
    try {
      const raw = JSON.parse(script.textContent || '[]');
      const seen = new Set();
      return raw.filter((item) => {
        if (!item?.id || seen.has(String(item.id))) return false;
        seen.add(String(item.id));
        item.specs = Array.isArray(item.specs)
          ? item.specs.filter((spec) => normalize(spec.label) && normalize(spec.value))
          : [];
        item.variants = Array.isArray(item.variants) ? item.variants : [];
        item.options = Array.isArray(item.options) ? item.options : [];
        return true;
      });
    } catch (error) {
      console.warn('[HiKNiGHT Compare] Invalid product JSON', error);
      return [];
    }
  }

  const SPEC_ORDER = [
    'feature',
    'motor',
    'torque',
    'battery',
    'top speed',
    'range',
    'suspension',
    'tires',
    'brakes',
    'max slope',
    'charging time',
    'transmission',
    'material',
    'bike weight',
    'max load',
    'rider height',
    'waterproof rating',
    'safety certification'
  ];

  function canonicalSpecKey(label) {
    const key = normalize(label)
      .replace(/[·•]/g, ' ')
      .replace(/\s+/g, ' ');

    if (!key) return '';
    if (key === 'feature' || key.includes('feature')) return 'feature';
    if (key.includes('peak motor') || key === 'motor' || key.includes('motor power') || key.includes('peak power')) return 'motor';
    if (key.includes('torque')) return 'torque';
    if (key.includes('battery')) return 'battery';
    if (key.includes('top speed') || key.includes('max speed')) return 'top speed';
    if (key.includes('mileage') || key === 'range' || key.includes('max range') || key.includes('maximum range')) return 'range';
    if (key.includes('suspension')) return 'suspension';
    if (key.includes('tire')) return 'tires';
    if (key.includes('brake')) return 'brakes';
    if (key.includes('slope') || key.includes('climb')) return 'max slope';
    if (key.includes('charging')) return 'charging time';
    if (key.includes('transmission') || key.includes('drivetrain') || key.includes('speed gear')) return 'transmission';
    if (key.includes('material') || key.includes('frame material')) return 'material';
    if (key.includes('bike weight') || key === 'weight') return 'bike weight';
    if (key.includes('payload') || key.includes('max load') || key.includes('maximum load')) return 'max load';
    if (key.includes('rider height') || key.includes('height range')) return 'rider height';
    if (key.includes('waterproof')) return 'waterproof rating';
    if (key.includes('certification') || key.includes('certified')) return 'safety certification';
    return key;
  }

  function getColorIndex(product) {
    return product.options.findIndex((name) => /colou?r/i.test(String(name || '')));
  }

  function colorVariants(product) {
    const index = getColorIndex(product);
    if (index < 0) return [];
    const seen = new Set();
    const values = [];

    product.variants.forEach((variant) => {
      const name = String(variant.options?.[index] || '').trim();
      const key = normalize(name);
      if (!key || seen.has(key)) return;
      seen.add(key);
      values.push({ name, variant });
    });

    return values;
  }

  function variantForColor(product, color) {
    const index = getColorIndex(product);
    if (index < 0) return null;
    return product.variants.find((variant) => normalize(variant.options?.[index]) === normalize(color)) || null;
  }

  function compareKind(product) {
    const signal = `${product?.title || ''} ${product?.handle || ''}`.toLowerCase();
    return /(^|[\s_-])combo([\s_-]|$)/i.test(signal) ? 'combo' : 'single';
  }

  function initModal(modal) {
    if (!modal || modal.dataset.hkCompareReady === 'true') return;
    modal.dataset.hkCompareReady = 'true';

    const sectionId = modal.dataset.sectionId;
    const currentId = String(modal.dataset.currentId || '');
    const group = modal.dataset.group || 'e-bike';
    const maxDesktop = Number(modal.dataset.maxDesktop || 3);
    const maxMobile = Number(modal.dataset.maxMobile || 2);
    const autoSecond = modal.dataset.autoSecond !== 'false';
    const sameSeriesProducts = parseProducts(modal).filter((p) => p.group === group);
    const currentProduct = sameSeriesProducts.find((p) => String(p.id) === currentId);
    const currentKind = compareKind(currentProduct);

    // Compare rules:
    // - Electric Bike only compares with Electric Bike
    // - Dirt Bike / Motorcycle only compares with the same series
    // - Single models never mix with Combo products
    // - Combo products only compare with other Combo products
    const products = sameSeriesProducts.filter((p) => compareKind(p) === currentKind);
    const byId = new Map(products.map((p) => [String(p.id), p]));

    const dialog = modal.querySelector('.hk-compare-modal__dialog');
    const table = modal.querySelector('[data-hk-compare-table]');
    const picker = modal.querySelector('[data-hk-compare-picker]');
    const pickerToggle = modal.querySelector('[data-hk-compare-picker-toggle]');
    const pickerList = modal.querySelector('[data-hk-compare-picker-list]');
    const limitMessage = modal.querySelector('[data-hk-compare-limit-message]');
    const diffToggle = modal.querySelector('[data-hk-compare-difference]');
    const removeAll = modal.querySelector('[data-hk-compare-remove-all]');

    const storageKey = `hiknight-compare:${group}:${currentId}`;
    let selected = [];
    let activeVariants = new Map();
    let lastFocus = null;
    let closingTimer = null;

    function currentMax() {
      return window.matchMedia('(max-width: 46.8125em)').matches ? maxMobile : maxDesktop;
    }

    function defaultSelection() {
      const defaults = [];

      if (byId.has(currentId)) {
        defaults.push(currentId);
      }

      if (autoSecond) {
        const second = products.find((p) => String(p.id) !== currentId);
        if (second) defaults.push(String(second.id));
      }

      return [...new Set(defaults)].slice(0, maxDesktop);
    }

    function loadSelection() {
      let restored = [];

      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored !== null) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            restored = parsed.map(String).filter((id) => byId.has(id));
          }
        }
      } catch (_) {
        restored = [];
      }

      // Never allow stale/empty storage to make a product PDP open blank.
      // Current PDP product is always the first item.
      if (byId.has(currentId)) {
        restored = restored.filter((id) => id !== currentId);
        restored.unshift(currentId);
      }

      selected = [...new Set(restored)].slice(0, maxDesktop);

      // First visit / stale empty state: current product + first eligible same-series model.
      if (!selected.length || (selected.length === 1 && autoSecond)) {
        const defaults = defaultSelection();
        defaults.forEach((id) => {
          if (!selected.includes(id) && selected.length < maxDesktop) {
            selected.push(id);
          }
        });
      }

      persist();
    }

    function persist() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(selected));
      } catch (_) {}
    }

    function productVariant(product) {
      const variantId = activeVariants.get(String(product.id));
      if (variantId) {
        const found = product.variants.find((v) => String(v.id) === String(variantId));
        if (found) return found;
      }
      const selected = product.variants.find((v) => String(v.id) === String(product.selectedVariantId || ''));
      return selected || product.variants[0] || null;
    }

    function buildRows(selectedProducts) {
      const rowMap = new Map();

      const addSpecs = (product) => {
        product.specs.forEach((spec) => {
          const key = canonicalSpecKey(spec.label);
          if (!key || rowMap.has(key)) return;
          rowMap.set(key, spec.label);
        });
      };

      const current = selectedProducts.find((p) => String(p.id) === currentId);
      if (current) addSpecs(current);
      selectedProducts.forEach((p) => {
        if (p !== current) addSpecs(p);
      });

      return [...rowMap.entries()]
        .sort(([a], [b]) => {
          const ai = SPEC_ORDER.indexOf(a);
          const bi = SPEC_ORDER.indexOf(b);
          if (ai === -1 && bi === -1) return a.localeCompare(b);
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        })
        .map(([key, label]) => ({ key, label }));
    }

    function specValue(product, row) {
      const key = row.key || canonicalSpecKey(row.label);
      const spec = product.specs.find((item) => canonicalSpecKey(item.label) === key);
      return spec?.value || '—';
    }

    function rowIsDifferent(values) {
      if (values.length < 2) return true;
      const normalized = values.map(normalize);
      return normalized.some((value) => value !== normalized[0]);
    }

    function renderPicker() {
      const max = currentMax();
      if (!pickerList) return;

      pickerList.innerHTML = products.map((product) => {
        const id = String(product.id);
        const checked = selected.includes(id);
        const disabled = !checked && selected.length >= max;
        return `
          <label class="hk-compare-picker__item${disabled ? ' is-disabled' : ''}">
            <input
              type="checkbox"
              value="${escapeHtml(id)}"
              ${checked ? 'checked' : ''}
              ${disabled ? 'disabled' : ''}
              data-hk-compare-product-check
            >
            <span>${escapeHtml(product.title)}</span>
          </label>
        `;
      }).join('');

      if (limitMessage) {
        limitMessage.textContent = selected.length >= max
          ? `You can compare up to ${max} models on this screen.`
          : '';
      }
    }

    function modelCell(product) {
      const variant = productVariant(product);
      const image = variant?.image || product.image;
      const price = variant?.price || product.price;
      const compareAt = variant?.compareAt || product.compareAt;
      const colors = colorVariants(product);
      const activeColorIndex = getColorIndex(product);
      const activeColor = activeColorIndex >= 0 ? variant?.options?.[activeColorIndex] : '';

      return `
        <div class="hk-compare-cell hk-compare-model-cell" data-product-id="${escapeHtml(product.id)}">
          <button type="button" class="hk-compare-model-cell__remove" data-hk-compare-remove="${escapeHtml(product.id)}" aria-label="Remove ${escapeHtml(product.title)}">×</button>
          <a href="${escapeHtml(product.url)}" class="hk-compare-model-cell__title">${escapeHtml(product.title)}</a>

          <div class="hk-compare-model-cell__image">
            ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="eager">` : ''}
          </div>

          ${colors.length ? `
            <div class="hk-compare-color-picker">
              <div class="hk-compare-color-picker__label">
                Color: <strong>${escapeHtml(activeColor || colors[0]?.name || '')}</strong>
              </div>
              <div class="hk-compare-color-thumbs" role="list" aria-label="${escapeHtml(product.title)} colors">
                ${colors.map(({ name, variant: colorVariant }) => {
                  const thumb = colorVariant?.image || product.image || image;
                  const isActive = normalize(name) === normalize(activeColor);
                  return `
                    <button
                      type="button"
                      class="hk-compare-color-thumb${isActive ? ' is-active' : ''}"
                      title="${escapeHtml(name)}"
                      aria-label="${escapeHtml(name)}"
                      aria-pressed="${isActive ? 'true' : 'false'}"
                      data-hk-compare-color="${escapeHtml(name)}"
                      data-product-id="${escapeHtml(product.id)}"
                    >
                      ${thumb ? `<img src="${escapeHtml(thumb)}" alt="${escapeHtml(name)}" loading="lazy">` : ''}
                    </button>
                  `;
                }).join('')}
              </div>
            </div>
          ` : ''}

          <div class="hk-compare-model-cell__price">
            ${escapeHtml(price || '')}
            ${compareAt ? `<span class="hk-compare-model-cell__compare">${escapeHtml(compareAt)}</span>` : ''}
          </div>

          <a href="${escapeHtml(product.url)}" class="hk-compare-view">View Product</a>
        </div>
      `;
    }

    function renderTable() {
      if (!table) return;

      const selectedProducts = selected.map((id) => byId.get(id)).filter(Boolean);
      table.style.setProperty('--hk-compare-count', String(Math.max(1, selectedProducts.length)));

      if (!selectedProducts.length) {
        table.innerHTML = `
          <div class="hk-compare-empty">
            <div>
              <strong>No models selected</strong>
              Use “Select Models” above to add products to the comparison.
            </div>
          </div>
        `;
        renderPicker();
        return;
      }

      const rows = buildRows(selectedProducts);
      const showDiff = diffToggle?.checked === true;
      const canCompareDifferences = selectedProducts.length > 1;
      let hiddenCount = 0;
      let visibleRows = 0;

      let html = `
        <div class="hk-compare-row">
          <div class="hk-compare-cell hk-compare-cell--label hk-compare-model-label">Model</div>
          ${selectedProducts.map(modelCell).join('')}
        </div>
      `;

      const priceValues = selectedProducts.map(
        (product) => productVariant(product)?.price || product.price || '—'
      );

      const hidePrice = showDiff && canCompareDifferences && !rowIsDifferent(priceValues);
      if (hidePrice) {
        hiddenCount += 1;
      } else {
        visibleRows += 1;
        html += `
          <div class="hk-compare-row">
            <div class="hk-compare-cell hk-compare-cell--label">Price</div>
            ${priceValues.map((value) => `<div class="hk-compare-cell hk-compare-value">${escapeHtml(value)}</div>`).join('')}
          </div>
        `;
      }

      rows.forEach((row) => {
        const values = selectedProducts.map((product) => specValue(product, row));
        const hideRow = showDiff && canCompareDifferences && !rowIsDifferent(values);

        if (hideRow) {
          hiddenCount += 1;
          return;
        }

        visibleRows += 1;
        html += `
          <div class="hk-compare-row">
            <div class="hk-compare-cell hk-compare-cell--label">${escapeHtml(row.label)}</div>
            ${values.map((value) => `<div class="hk-compare-cell hk-compare-value">${escapeHtml(value)}</div>`).join('')}
          </div>
        `;
      });

      if (showDiff && canCompareDifferences && hiddenCount > 0 && visibleRows === 0) {
        html += `
          <div class="hk-compare-row">
            <div class="hk-compare-cell hk-compare-cell--label">Difference</div>
            <div class="hk-compare-cell hk-compare-value" style="grid-column:span ${selectedProducts.length};">
              No different specifications found.
            </div>
          </div>
        `;
      }

      table.innerHTML = html;
      table.dataset.hkDifferenceHidden = String(hiddenCount);

      const diffLabel = diffToggle?.closest('.hk-compare-difference');
      if (diffLabel) {
        diffLabel.classList.toggle('is-active', showDiff);
        diffLabel.classList.toggle('is-disabled', !canCompareDifferences);

        if (!canCompareDifferences) {
          diffLabel.title = 'Select at least two models to compare differences.';
        } else if (showDiff) {
          diffLabel.title = hiddenCount
            ? `${hiddenCount} identical row${hiddenCount === 1 ? '' : 's'} hidden.`
            : 'All visible specifications are different.';
        } else {
          diffLabel.title = 'Showing all specifications.';
        }
      }

      renderPicker();
    }

    function setSelected(id, checked) {
      id = String(id);
      if (!byId.has(id)) return;

      const max = currentMax();
      if (checked && !selected.includes(id)) {
        if (selected.length >= max) {
          if (limitMessage) limitMessage.textContent = `You can compare up to ${max} models on this screen.`;
          return;
        }
        selected.push(id);
      } else if (!checked) {
        selected = selected.filter((item) => item !== id);
        activeVariants.delete(id);
      }

      selected = [...new Set(selected)];
      persist();
      renderTable();
    }

    function openPicker() {
      if (!picker || !pickerToggle) return;
      picker.hidden = false;
      pickerToggle.setAttribute('aria-expanded', 'true');
    }

    function closePicker() {
      if (!picker || !pickerToggle) return;
      picker.hidden = true;
      pickerToggle.setAttribute('aria-expanded', 'false');
    }

    function openModal(trigger) {
      if (closingTimer) {
        clearTimeout(closingTimer);
        closingTimer = null;
      }

      if (!selected.length) {
        selected = defaultSelection();
        persist();
      }

      lastFocus = trigger || document.activeElement;
      if (!modal.isConnected || modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
      modal.hidden = false;
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.documentElement.classList.add('hk-compare-open');
      closePicker();
      renderTable();
      window.setTimeout(() => dialog?.focus(), 40);
    }

    function closeModal() {
      closePicker();
      modal.classList.remove('is-open');
      document.documentElement.classList.remove('hk-compare-open');
      closingTimer = window.setTimeout(() => {
        modal.hidden = true;
        closingTimer = null;
      }, 220);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    loadSelection();
    renderTable();

    modal.addEventListener('click', (event) => {
      const close = event.target.closest('[data-hk-compare-close]');
      if (close) {
        event.preventDefault();
        closeModal();
        return;
      }

      const remove = event.target.closest('[data-hk-compare-remove]');
      if (remove) {
        event.preventDefault();
        setSelected(remove.dataset.hkCompareRemove, false);
        return;
      }

      const swatch = event.target.closest('[data-hk-compare-color]');
      if (swatch) {
        event.preventDefault();
        const product = byId.get(String(swatch.dataset.productId));
        const variant = product ? variantForColor(product, swatch.dataset.hkCompareColor) : null;
        if (product && variant) {
          activeVariants.set(String(product.id), String(variant.id));
          renderTable();
        }
      }
    });

    pickerToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      picker?.hidden ? openPicker() : closePicker();
    });

    pickerList?.addEventListener('change', (event) => {
      const input = event.target.closest('[data-hk-compare-product-check]');
      if (!input) return;
      setSelected(input.value, input.checked);
    });

    removeAll?.addEventListener('click', () => {
      selected = [];
      activeVariants.clear();
      persist();
      renderTable();
    });

    if (diffToggle) {
      diffToggle.addEventListener('change', renderTable);
      diffToggle.addEventListener('input', renderTable);
    }

    document.addEventListener('click', (event) => {
      if (picker?.hidden) return;
      if (event.target.closest('.hk-compare-picker')) return;
      closePicker();
    });

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        if (picker && !picker.hidden) {
          closePicker();
        } else {
          closeModal();
        }
      }
    });

    window.addEventListener('resize', renderPicker, { passive: true });

    modal.hkCompareOpen = openModal;
  }

  function initAll() {
    document.querySelectorAll(MODAL_SELECTOR).forEach(initModal);

    document.querySelectorAll(OPEN_SELECTOR).forEach((button) => {
      if (button.dataset.hkCompareTriggerReady === 'true') return;
      button.dataset.hkCompareTriggerReady = 'true';
      button.addEventListener('click', () => {
        const id = button.getAttribute('aria-controls');
        const modal = id ? document.getElementById(id) : null;
        if (modal?.hkCompareOpen) {
          modal.hkCompareOpen(button);
        } else if (modal) {
          initModal(modal);
          modal.hkCompareOpen?.(button);
        }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  initAll();
})();
